import { Link } from "react-router-dom";
import { Navbar, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Home, Building2, Hammer, Wrench, PaintBucket, Ruler, 
  ArrowRight, CheckCircle, Phone
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_de12da21-0869-4a9b-8efe-c2e2b4d72064/artifacts/km3uhq32_YGK-CONSTRUCTION.png";

const ServicesPage = () => {
  const services = [
    {
      icon: Home,
      title: "Custom Home Building",
      description: "From foundation to finishing touches, we build custom homes that reflect your lifestyle and exceed your expectations.",
      features: ["Architectural planning", "Foundation & framing", "Interior finishing", "Landscaping coordination"]
    },
    {
      icon: Hammer,
      title: "Home Renovations",
      description: "Transform your existing space with our expert renovation services. Kitchen, bathroom, basement—we do it all.",
      features: ["Kitchen remodels", "Bathroom renovations", "Basement finishing", "Whole-house updates"]
    },
    {
      icon: Building2,
      title: "Commercial Construction",
      description: "Professional commercial builds for offices, retail spaces, restaurants, and industrial facilities.",
      features: ["Tenant improvements", "Retail fit-outs", "Restaurant builds", "Office renovations"]
    },
    {
      icon: Wrench,
      title: "Additions & Expansions",
      description: "Need more space? We seamlessly integrate additions that match your home's existing architecture.",
      features: ["Room additions", "Second stories", "Garage conversions", "Sunroom additions"]
    },
    {
      icon: Ruler,
      title: "Structural Work",
      description: "Expert structural modifications including load-bearing wall removal, beam installation, and foundation repairs.",
      features: ["Wall removal", "Beam installation", "Foundation work", "Structural repairs"]
    },
    {
      icon: PaintBucket,
      title: "Finishing Services",
      description: "Complete interior and exterior finishing including drywall, painting, trim work, and flooring installation.",
      features: ["Drywall & taping", "Interior painting", "Trim & millwork", "Flooring installation"]
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-black overflow-hidden">
        {/* Background logo */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: '400px',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.05,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Our Services</p>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-6">
              Building Excellence in <span className="text-orange-500">Every Detail</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              From residential renovations to commercial builds, our comprehensive services 
              cover every aspect of construction with quality craftsmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-zinc-950 bg-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="bg-zinc-900/50 border-2 border-zinc-800 hover:border-orange-500 transition-all duration-300 card-hover group backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-orange-500/20 group-hover:bg-orange-500 transition-colors flex items-center justify-center mb-6">
                    <service.icon className="h-7 w-7 text-orange-500 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 leading-relaxed mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-400">
                        <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-orange-500 font-semibold tracking-wider uppercase mb-3">Our Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              How We Work
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "We discuss your vision, needs, and budget to understand your project fully." },
              { step: "02", title: "Planning", desc: "Our team creates detailed plans and provides a transparent, itemized quote." },
              { step: "03", title: "Construction", desc: "Expert craftsmen bring your project to life with quality materials and precision." },
              { step: "04", title: "Completion", desc: "Final walkthrough ensures everything meets our standards and your expectations." }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-black text-orange-500/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-500">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Contact us today for a free consultation and estimate on your project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimator">
              <Button size="lg" className="bg-black text-orange-500 hover:bg-zinc-900 font-semibold text-lg px-8 py-6">
                Get Free Estimate
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:4052562176">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-orange-500 font-semibold text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                (405) 256-2176
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
